import React, { useMemo, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { addVarietyToProduct, uploadFile, addVarietyPriceToVariety } from '../api';
import { FiUpload, FiTrash2 } from 'react-icons/fi';
import { normalizeShortBarcode, sanitizeShortBarcodeInput } from '../utils/barcode';
import { CLIENT_TYPE_OPTIONS, resolvePriceClientType, getClientTypeLabel } from '../utils/clientType';
import InlineLoading from './InlineLoading';

const NewVarietyModal = ({
    isOpen,
    onClose,
    product,
    onVarietyCreated,
    existingVarieties,
    barcodeScope,
    canUpdateProduct = true,
}) => {
    const [varietyName, setVarietyName] = useState('');
    const [description, setDescription] = useState('');
    const [varietyImage, setVarietyImage] = useState(null);
    const [file, setFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [varietyPrices, setVarietyPrices] = useState([]);
    const [inheritGlobalRule, setInheritGlobalRule] = useState(true);
    const [priceRuleMode, setPriceRuleMode] = useState('quantity');
    const [priceRuleThresholds, setPriceRuleThresholds] = useState({
        detalle: '',
        mayorista: '',
        importador: '',
    });
    const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');

    const fileInputs = useRef([]);
    const productId = product?.id;
    const effectiveBarcodeScope = barcodeScope === 'per_variety' ? 'per_variety' : 'global';
    const varietyNameMap = useMemo(() => {
        const map = new Map();
        (existingVarieties || []).forEach((variety) => {
            if (variety?.id) {
                map.set(variety.id, variety.varietyName || `Variedad ${variety.id}`);
            }
        });
        return map;
    }, [existingVarieties]);
    const existingPriceEntries = useMemo(() => {
        const entries = [];
        (existingVarieties || []).forEach((variety) => {
            (variety?.VarietyPrices || []).forEach((price) => {
                const barcode = normalizeShortBarcode(price.barcode);
                if (!barcode) return;
                entries.push({
                    varietyId: variety.id,
                    barcode,
                    clientType: resolvePriceClientType(price),
                });
            });
        });
        return entries;
    }, [existingVarieties]);

    if (!isOpen || !product) return null;

    const getBarcodeWarning = (price, index) => {
        const barcode = normalizeShortBarcode(price.barcode);
        if (!barcode) return null;
        const clientType = resolvePriceClientType(price);
        const label = getClientTypeLabel(clientType);
        const duplicateInNew = varietyPrices.some((entry, idx) => {
            if (idx === index) return false;
            const entryBarcode = normalizeShortBarcode(entry.barcode);
            if (!entryBarcode) return false;
            return entryBarcode === barcode && resolvePriceClientType(entry) === clientType;
        });
        const matchesExisting = effectiveBarcodeScope === 'global'
            ? existingPriceEntries.filter(
                (entry) => entry.barcode === barcode && entry.clientType === clientType
            )
            : [];
        if (!duplicateInNew && matchesExisting.length === 0) {
            return null;
        }
        if (!matchesExisting.length) {
            return `Código de barras repetido para ${label} en esta variedad.`;
        }
        const varietyNames = matchesExisting
            .map((entry) => varietyNameMap.get(entry.varietyId))
            .filter(Boolean);
        const uniqueNames = Array.from(new Set(varietyNames));
        if (uniqueNames.length) {
            return `Código de barras ya existe para ${label} en: ${uniqueNames.join(', ')}.`;
        }
        return `Código de barras ya existe para ${label} en otra variedad.`;
    };

    const handleAddPrice = () => {
        setVarietyPrices([
            ...varietyPrices,
            {
                price: '',
                metricUnit: '',
                priceImage: null,
                file: null,
                barcode: '',
                clientType: 'mayorista',
                useScaleWeight: false,
                unitWeightKg: '',
            },
        ]);
    };

    const handleDeletePrice = (index) => {
        setVarietyPrices(varietyPrices.filter((_, i) => i !== index));
    };

    const handleCreateVariety = async () => {
        if (!canUpdateProduct || !productId) return;
        setIsLoading(true);

        try {
            let uploadedImageUrl = null;
            if (file) {
                const response = await uploadFile(file);
                uploadedImageUrl = response.url;
            }

            // Create Variety
            const varietyRulePayload = inheritGlobalRule
                ? null
                : {
                    mode: priceRuleMode === 'subtotal' ? 'subtotal' : 'quantity',
                    thresholds: {
                        detalle: priceRuleThresholds.detalle === '' ? null : Number(priceRuleThresholds.detalle),
                        mayorista: priceRuleThresholds.mayorista === '' ? null : Number(priceRuleThresholds.mayorista),
                        importador: priceRuleThresholds.importador === '' ? null : Number(priceRuleThresholds.importador),
                    },
                };
            var newVariety = await addVarietyToProduct(branchId, {
                varietyName,
                description,
                varietyImage: uploadedImageUrl,
                productId,
                priceAutoRuleOverride: varietyRulePayload,
            });

            let varietyPriceObjects = [];
            // Upload Prices
            for (const price of varietyPrices) {
                let priceImageUrl = null;
                if (price.file) {
                    const imgResponse = await uploadFile(price.file);
                    priceImageUrl = imgResponse.url;
                }

                const normalizedBarcode = normalizeShortBarcode(price.barcode);
                const varietyPrice = await addVarietyPriceToVariety(branchId, {
                    varietyId: newVariety.id,
                    price: price.price,
                    metricUnit: price.metricUnit,
                    priceImage: priceImageUrl,
                    barcode: normalizedBarcode,
                    clientType: price.clientType || 'mayorista',
                    useScaleWeight: Boolean(price.useScaleWeight),
                    unitWeightKg: String(price.unitWeightKg ?? '').trim() || null,
                });
                varietyPriceObjects.push(varietyPrice);
            }
            newVariety.VarietyPrices = varietyPriceObjects;
            onVarietyCreated(newVariety); // Refresh parent component
            onClose();
        } catch (error) {
            console.error('Error creating variety:', error);
        } finally {
            setIsLoading(false);
            setDescription('');
            setVarietyName('');
            setVarietyImage(null);
            setFile(null);
            setVarietyPrices([]);
            setInheritGlobalRule(true);
            setPriceRuleMode('quantity');
            setPriceRuleThresholds({
                detalle: '',
                mayorista: '',
                importador: '',
            });
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 px-4 py-6 overflow-y-auto"
            onClick={(event) => event.stopPropagation()}
        >
            <div
                className="bg-white w-full max-w-5xl rounded-2xl shadow-2xl border border-gray-100 overflow-hidden max-h-[92vh] sm:max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100 bg-gray-50 shrink-0">
                    <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500">Producto</p>
                        <h2 className="text-lg font-semibold text-gray-900">
                            Agregar tipo a {product.productName}
                        </h2>
                        <p className="text-xs text-gray-500 mt-1">
                            Define variedad, precios y códigos. Las reglas de precios y estado se aplican por tipo de cliente.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-full w-9 h-9 flex items-center justify-center"
                        aria-label="Cerrar"
                    >
                        ✕
                    </button>
                </div>

                <div className="p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-4 sm:gap-6 overflow-y-auto min-h-0">
                    <div className="space-y-3 sm:space-y-4">
                        <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 shadow-sm">
                            <div className="text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Imagen de la variedad</div>
                            <div className="relative mx-auto w-24 h-24 sm:w-32 sm:h-32 lg:w-full lg:aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                                <img
                                    src={varietyImage || 'https://placehold.co/400'}
                                    alt="Variedad"
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                            </div>
                            <div className="mt-3 flex gap-2">
                                <label className="flex-1 cursor-pointer">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        disabled={isLoading || !canUpdateProduct}
                                        onChange={(e) => {
                                            if (!e.target.files?.[0]) return;
                                            setVarietyImage(URL.createObjectURL(e.target.files[0]));
                                            setFile(e.target.files[0]);
                                        }}
                                    />
                                    <span className="block text-center text-sm font-medium px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
                                        Subir imagen
                                    </span>
                                </label>
                                {varietyImage && (
                                    <button
                                        type="button"
                                        className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:text-gray-800"
                                        onClick={() => {
                                            setVarietyImage(null);
                                            setFile(null);
                                        }}
                                        disabled={isLoading || !canUpdateProduct}
                                    >
                                        Quitar
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-3">
                            <div>
                                <label className="text-xs font-semibold text-gray-600 uppercase">Nombre de variedad</label>
                                <input
                                    type="text"
                                    placeholder="Ej: Bolsa 1kg"
                                    className="mt-2 border border-gray-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-blue-200"
                                    value={varietyName}
                                    onChange={(e) => setVarietyName(e.target.value)}
                                    disabled={!canUpdateProduct}
                                />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-gray-600 uppercase">Descripción</label>
                                <textarea
                                    placeholder="Detalle breve de la variedad"
                                    className="mt-2 border border-gray-300 rounded-lg p-2 w-full min-h-[96px] focus:ring-2 focus:ring-blue-200"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    disabled={!canUpdateProduct}
                                />
                            </div>
                            <div className="text-xs text-gray-500">
                                Barcode scope: {effectiveBarcodeScope === 'per_variety' ? 'por variedad' : 'global'}.
                            </div>
                            <div className="pt-2 border-t border-gray-100 space-y-2">
                                <label className="flex items-center gap-2 text-xs text-gray-700">
                                    <input
                                        type="checkbox"
                                        checked={inheritGlobalRule}
                                        onChange={(e) => setInheritGlobalRule(e.target.checked)}
                                        disabled={isLoading || !canUpdateProduct}
                                    />
                                    Heredar regla global de selección automática
                                </label>
                                {!inheritGlobalRule && (
                                    <div className="space-y-2">
                                        <select
                                            className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                                            value={priceRuleMode}
                                            onChange={(e) => setPriceRuleMode(e.target.value)}
                                            disabled={isLoading || !canUpdateProduct}
                                        >
                                            <option value="quantity">Cantidad por variedad</option>
                                            <option value="subtotal">Subtotal documento</option>
                                        </select>
                                        <div className="grid grid-cols-1 gap-2">
                                            {['detalle', 'mayorista', 'importador'].map((type) => (
                                                <input
                                                    key={type}
                                                    type="text"
                                                    inputMode="numeric"
                                                    placeholder={`Umbral ${type}`}
                                                    className="border border-gray-300 rounded-lg p-2 text-sm"
                                                    value={priceRuleThresholds[type]}
                                                    onChange={(e) =>
                                                        setPriceRuleThresholds((prev) => ({
                                                            ...prev,
                                                            [type]: String(e.target.value || '').replace(/[^\d]/g, ''),
                                                        }))
                                                    }
                                                    disabled={isLoading || !canUpdateProduct}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Precios por cliente</h3>
                                <p className="text-xs text-gray-500">
                                    Agrega uno o varios precios. Se validan códigos de barras por tipo de cliente.
                                </p>
                            </div>
                            {canUpdateProduct && (
                                <button
                                    className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700"
                                    onClick={handleAddPrice}
                                >
                                    + Nuevo precio
                                </button>
                            )}
                        </div>

                        <div className="space-y-3">
                            {varietyPrices.length === 0 && (
                                <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center text-sm text-gray-500">
                                    Aún no agregas precios. Usa “Nuevo precio” para comenzar.
                                </div>
                            )}
                            {varietyPrices.map((price, index) => {
                                const barcodeWarning = getBarcodeWarning(price, index);
                                return (
                                    <div
                                        key={index}
                                        className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm"
                                    >
                                        <div className="flex flex-col md:flex-row md:items-start gap-4">
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    ref={(input) => (fileInputs.current[index] = input)}
                                                    className="hidden"
                                                    disabled={!canUpdateProduct}
                                                    onChange={(e) => {
                                                        if (e.target.files?.length > 0) {
                                                            const updatedPrices = [...varietyPrices];
                                                            updatedPrices[index].file = e.target.files[0];
                                                            updatedPrices[index].priceImage = URL.createObjectURL(e.target.files[0]);
                                                            setVarietyPrices(updatedPrices);
                                                        }
                                                    }}
                                                />
                                                <div className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                                                    <img
                                                        src={price.priceImage || varietyImage || 'https://placehold.co/80'}
                                                        alt="Precio"
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                                {canUpdateProduct && (
                                                    <button
                                                        type="button"
                                                        className="px-2 py-1 rounded-lg border border-gray-200 text-xs text-gray-600 hover:text-gray-800"
                                                        onClick={() => fileInputs.current[index]?.click()}
                                                    >
                                                        Cambiar imagen
                                                    </button>
                                                )}
                                            </div>

                                            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                                                <div>
                                                    <label className="text-xs font-semibold text-gray-600 uppercase">Precio</label>
                                                    <input
                                                        className="mt-2 border border-gray-300 rounded-lg p-2 w-full"
                                                        type="number"
                                                        value={price.price}
                                                        onChange={(e) => {
                                                            const updatedPrices = [...varietyPrices];
                                                            updatedPrices[index].price = e.target.value;
                                                            setVarietyPrices(updatedPrices);
                                                        }}
                                                        disabled={!canUpdateProduct}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-semibold text-gray-600 uppercase">Unidad</label>
                                                    <input
                                                        className="mt-2 border border-gray-300 rounded-lg p-2 w-full"
                                                        type="text"
                                                        value={price.metricUnit}
                                                        onChange={(e) => {
                                                            const updatedPrices = [...varietyPrices];
                                                            updatedPrices[index].metricUnit = e.target.value;
                                                            setVarietyPrices(updatedPrices);
                                                        }}
                                                        disabled={!canUpdateProduct}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-semibold text-gray-600 uppercase">Tipo de cliente</label>
                                                    <select
                                                        className="mt-2 border border-gray-300 rounded-lg p-2 w-full"
                                                        value={price.clientType || 'mayorista'}
                                                        onChange={(e) => {
                                                            const updatedPrices = [...varietyPrices];
                                                            updatedPrices[index].clientType = e.target.value;
                                                            setVarietyPrices(updatedPrices);
                                                        }}
                                                        disabled={!canUpdateProduct}
                                                    >
                                                        {CLIENT_TYPE_OPTIONS.map((option) => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="text-xs font-semibold text-gray-600 uppercase">Código de barras</label>
                                                    <input
                                                        className="mt-2 border border-gray-300 rounded-lg p-2 w-full"
                                                        type="text"
                                                        value={price.barcode ?? ''}
                                                        placeholder="Código de barras"
                                                        inputMode="numeric"
                                                        maxLength={7}
                                                        pattern="[0-9]*"
                                                        onChange={(e) => {
                                                            const updatedPrices = [...varietyPrices];
                                                            updatedPrices[index].barcode = sanitizeShortBarcodeInput(e.target.value);
                                                            setVarietyPrices(updatedPrices);
                                                        }}
                                                        disabled={!canUpdateProduct}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-semibold text-gray-600 uppercase">Peso desde balanza</label>
                                                    <label className="mt-2 inline-flex items-center gap-2 text-sm text-gray-700">
                                                        <input
                                                            type="checkbox"
                                                            checked={Boolean(price.useScaleWeight)}
                                                            onChange={(e) => {
                                                                const updatedPrices = [...varietyPrices];
                                                                updatedPrices[index].useScaleWeight = e.target.checked;
                                                                setVarietyPrices(updatedPrices);
                                                            }}
                                                            disabled={!canUpdateProduct}
                                                        />
                                                        Autocompletar desde balanza
                                                    </label>
                                                </div>
                                                <div>
                                                    <label className="text-xs font-semibold text-gray-600 uppercase">Peso unidad (kg)</label>
                                                    <input
                                                        className="mt-2 border border-gray-300 rounded-lg p-2 w-full"
                                                        type="number"
                                                        min="0"
                                                        step="0.001"
                                                        value={price.unitWeightKg ?? ''}
                                                        onChange={(e) => {
                                                            const updatedPrices = [...varietyPrices];
                                                            updatedPrices[index].unitWeightKg = e.target.value;
                                                            setVarietyPrices(updatedPrices);
                                                        }}
                                                        disabled={!canUpdateProduct}
                                                    />
                                                </div>
                                            </div>

                                            {canUpdateProduct && (
                                                <div className="flex md:flex-col gap-2 md:items-end">
                                                    <button
                                                        className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 flex items-center gap-2"
                                                        onClick={() => handleDeletePrice(index)}
                                                    >
                                                        <FiTrash2 size={16} />
                                                        Quitar
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        {barcodeWarning && (
                                            <div className="mt-3 text-xs text-orange-600 bg-orange-50 border border-orange-100 rounded-lg px-3 py-2">
                                                {barcodeWarning}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
                    <div className="text-xs text-gray-500">
                        Guarda para aplicar precios y códigos de barras a la nueva variedad.
                    </div>
                    <div className="flex gap-2">
                        <button
                            className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:text-gray-900"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancelar
                        </button>
                        {canUpdateProduct && (
                            <button
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                                onClick={handleCreateVariety}
                                disabled={isLoading || !varietyName.trim()}
                            >
                                {isLoading ? <InlineLoading label="Guardando" /> : 'Guardar'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewVarietyModal;
